
import { defineComponent, ref, computed, toRefs, watch } from "vue";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import { ElMessage } from "element-plus";
import { BoxInfoSimulationResult } from "@/store/modules/BoxInfoModule";

export default defineComponent({
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    boxInfo: {
      type: Object as () => any | null,
      default: null,
    },
  },
  emits: ["update:visible"],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setup(props, { emit }) {
    const store = useStore();
    const { boxInfo, visible } = toRefs(props);

    const isLevel = computed(() => boxInfo.value?.game_type === "level");
    const simulationCount = ref(10000);
    const loading = ref(false);
    const result = ref<BoxInfoSimulationResult | null>(null);

    const ssrHitRate = computed(() => {
      if (!result.value || !result.value.times || result.value.times === 0)
        return "0.00";
      return (
        ((result.value.total_sssr_count || 0) / result.value.times) *
        100
      ).toFixed(2);
    });

    const doubleRate = computed(() => {
      if (!result.value || !result.value.times || result.value.times === 0)
        return "0.00";
      return (
        ((result.value.total_double_count || 0) / result.value.times) *
        100
      ).toFixed(2);
    });

    const boxInBoxRate = computed(() => {
      if (!result.value || !result.value.times || result.value.times === 0)
        return "0.00";
      return (
        ((result.value.box_in_box_triggers || 0) / result.value.times) *
        100
      ).toFixed(2);
    });

    const getLevelTagType = (level: string) => {
      if (level === "SSSR") return "danger";
      if (level === "SSR") return "warning";
      if (level === "SR") return "success";
      return "info";
    };

    const getGiftTagType = (giftType: string) => {
      if (giftType === "product") return "danger";
      if (giftType === "extra_point") return "warning";
      if (giftType === "membership_point") return "success";
      return "info";
    };

    const getGiftLabel = (giftType: string) => {
      const map: Record<string, string> = {
        no_gift: "无奖励",
        membership_point: "积分",
        extra_point: "探玩石",
        product: "商品",
      };
      return map[giftType] || giftType;
    };

    const getProgressColor = (percentage: number) => {
      if (percentage >= 30) return "#F56C6C";
      if (percentage >= 15) return "#E6A23C";
      return "#67C23A";
    };

    const runSimulation = async () => {
      if (!boxInfo.value?.id) {
        ElMessage.error("请先选择套系");
        return;
      }

      loading.value = true;
      try {
        const data = await store.dispatch(Actions.SIMULATE_BOX_INFO, {
          id: boxInfo.value.id,
          times: simulationCount.value,
        });
        result.value = data;
      } catch (error: unknown) {
        const errorMsg =
          (error as { error_msg?: string })?.error_msg || "模拟失败";
        ElMessage.error(errorMsg);
      } finally {
        loading.value = false;
      }
    };

    watch(visible, (newVal) => {
      if (newVal) {
        result.value = null;
        simulationCount.value = isLevel.value ? 1000 : 10000;
      }
    });

    return {
      simulationCount,
      loading,
      result,
      isLevel,
      ssrHitRate,
      doubleRate,
      boxInBoxRate,
      getLevelTagType,
      getGiftTagType,
      getGiftLabel,
      getProgressColor,
      runSimulation,
    };
  },
});
